import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingDown, TrendingUp, Minus, Sparkles, RefreshCw, Loader2,
  AlertTriangle, CheckCircle2, Clock, ArrowRight, Calendar,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  on_track:    { label: 'ON TRACK',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  at_risk:     { label: 'AT RISK',     cls: 'bg-amber-50   text-amber-700   border-amber-200',   icon: AlertTriangle },
  escalated:   { label: 'ESCALATED',   cls: 'bg-red-50     text-red-700     border-red-200',     icon: AlertTriangle },
  resolved:    { label: 'RESOLVED',    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  no_response: { label: 'NO REPLY',    cls: 'bg-slate-50   text-slate-600   border-slate-200',   icon: Clock },
};

const VERDICT_META = {
  on_track:   { label: 'ON TRACK',   cls: 'bg-emerald-50 text-emerald-800 border-emerald-300', icon: TrendingUp   },
  improving:  { label: 'IMPROVING',  cls: 'bg-emerald-50 text-emerald-800 border-emerald-300', icon: TrendingUp   },
  slipping:   { label: 'SLIPPING',   cls: 'bg-amber-50   text-amber-800   border-amber-300',   icon: TrendingDown },
  stalled:    { label: 'STALLED',    cls: 'bg-red-50     text-red-800     border-red-300',     icon: TrendingDown },
  first_audit:{ label: 'FIRST AUDIT',cls: 'bg-slate-50   text-slate-700   border-slate-200',   icon: Minus        },
};

function WeekPill({ sib, onClick }) {
  const status = STATUS_META[sib.overall_status] || STATUS_META.no_response;
  const StatusIcon = status.icon;
  const isCurrent = sib.is_current;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isCurrent}
      data-testid={`series-week-${sib.week_number || 'none'}`}
      className={`group relative flex flex-col gap-1 min-w-[128px] px-3 py-2.5 rounded-xl border text-left transition-all
        ${isCurrent
          ? 'bg-primary/5 border-primary/60 ring-2 ring-primary/30 cursor-default shadow-[0_0_0_4px_rgba(59,130,246,0.06)]'
          : 'bg-white border-border/70 hover:border-primary/40 hover:shadow-sm cursor-pointer'}`}
      title={sib.thread_subject || ''}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-bold tracking-wider ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
          {sib.week_number != null ? `W${sib.week_number}` : 'W—'}
        </span>
        {isCurrent && (
          <span className="text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded">
            YOU ARE HERE
          </span>
        )}
      </div>
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${status.cls}`}>
        <StatusIcon className="w-2.5 h-2.5" />
        {status.label}
      </span>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
        {sib.high_alert_count > 0 && (
          <span className="text-red-600 font-semibold">{sib.high_alert_count}H</span>
        )}
        {sib.alert_count > 0 && (
          <span className="text-amber-600 font-semibold">{sib.alert_count}A</span>
        )}
        <span>·</span>
        <span>{sib.total_skus || 0} SKUs</span>
      </div>
    </button>
  );
}

function CarryoverTable({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mt-4" data-testid="carryover-skus">
      <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <AlertTriangle className="w-3 h-3 text-red-500" />
        SKU Carry-over — still flagged across weeks
      </div>
      <div className="rounded-lg border border-border/60 overflow-hidden">
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-1.5 font-semibold">Model</th>
              <th className="text-left px-3 py-1.5 font-semibold">First flagged</th>
              <th className="text-left px-3 py-1.5 font-semibold">Latest status</th>
              <th className="text-right px-3 py-1.5 font-semibold">Weeks persisted</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 12).map((r, i) => {
              const sev = (r.latest_status || '').toUpperCase();
              const sevCls = sev === 'HIGH_ALERT' ? 'bg-red-100 text-red-700'
                          : sev === 'ALERT'      ? 'bg-amber-100 text-amber-700'
                          : 'bg-slate-100 text-slate-600';
              return (
                <tr key={i} className="border-t border-border/50 hover:bg-slate-50/70">
                  <td className="px-3 py-1.5 font-medium text-foreground">{r.sku}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {r.first_flagged_week != null ? `W${r.first_flagged_week}` : '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${sevCls}`}>
                      {sev || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-bold text-red-600">
                    {r.weeks_persisted || '?'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length > 12 && (
          <div className="px-3 py-1 bg-slate-50 text-[10px] text-muted-foreground italic">
            +{rows.length - 12} more
          </div>
        )}
      </div>
    </div>
  );
}

function CommitmentOutcomes({ rows }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mt-4" data-testid="commitment-outcomes">
      <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
        <CheckCircle2 className="w-3 h-3 text-primary" />
        Prior-week commitments — outcome this week
      </div>
      <ul className="space-y-1.5">
        {rows.map((c, i) => {
          const out = (c.outcome || '').toLowerCase();
          const meta = out === 'honoured'
              ? { cls: 'border-emerald-200 bg-emerald-50/60 text-emerald-900', chip: 'bg-emerald-200/70 text-emerald-900', label: 'HONOURED' }
            : out === 'missed'
              ? { cls: 'border-red-200      bg-red-50/60      text-red-900',     chip: 'bg-red-200/70      text-red-900',     label: 'MISSED'   }
              : { cls: 'border-amber-200    bg-amber-50/60    text-amber-900',   chip: 'bg-amber-200/70    text-amber-900',   label: 'PENDING'  };
          return (
            <li key={i} className={`rounded-lg border px-3 py-2 ${meta.cls}`}>
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${meta.chip}`}>
                  {meta.label}
                </span>
                {c.from_week != null && (
                  <span className="text-[10px] font-semibold text-muted-foreground">
                    from W{c.from_week}
                  </span>
                )}
                {c.who && (
                  <span className="text-[11px] font-semibold">{c.who}</span>
                )}
              </div>
              <div className="text-[12px] leading-snug">{c.what}</div>
              {c.evidence && (
                <div className="mt-1 text-[10px] italic opacity-80 truncate">
                  "{c.evidence}"
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function SeriesProgressionCard({ auditId, siblings }) {
  const navigate = useNavigate();
  const [summary, setSummary]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [generating, setGenerating] = useState(false);
  // True only on the auto-run that fires on mount when the cached summary is
  // stale (or absent) — used to show a subtle "Auto-refreshed because…" hint
  // instead of the standard "regenerating…" pill that appears on manual clicks.
  const [autoRan, setAutoRan]     = useState(false);

  const fetchCached = useCallback(async () => {
    if (!auditId) return null;
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/audits/${auditId}/cross-series-summary`);
      if (r.ok) {
        const d = await r.json();
        const s = d.cross_series_summary || null;
        setSummary(s);
        return s;
      }
    } finally {
      setLoading(false);
    }
    return null;
  }, [auditId]);

  const regenerate = useCallback(async ({ silent = false } = {}) => {
    if (!auditId) return;
    setGenerating(true);
    try {
      const r = await fetch(`${BACKEND}/api/audits/${auditId}/cross-series-summary`, { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        setSummary(d.cross_series_summary || null);
        if (silent) setAutoRan(true);
      }
    } finally {
      setGenerating(false);
    }
  }, [auditId]);

  // ── Auto-run on mount when the cached verdict is stale ───────────────
  // We rely on the backend's `cross_series_summary_stale` flag (computed
  // against each prior week's thread.last_fetched_at and progress_summary
  // generated_at) so we only burn a Bedrock call when it actually matters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = await fetchCached();
      if (cancelled) return;
      const shouldAutoRun = siblings?.has_series && siblings?.cross_series_summary_stale;
      if (shouldAutoRun) {
        await regenerate({ silent: true });
      }
    })();
    return () => { cancelled = true; };
    // siblings? changes only when the parent fetches a new payload, which is
    // a fresh page mount — exactly when we want to re-evaluate staleness.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId, siblings?.cross_series_summary_stale, siblings?.has_series]);

  const hasSeries     = siblings?.has_series;
  const sibs          = siblings?.siblings || [];
  const seriesDisplay = siblings?.series_display || siblings?.series_key;
  const priorCount    = (summary?.prior_week_count) ?? Math.max(0, sibs.filter(s => !s.is_current).length);
  const isStale       = !!siblings?.cross_series_summary_stale;
  const stalenessReason = siblings?.staleness_reason || '';

  // Friendly label for the auto-run hint — derive the prior week from the
  // backend's reason code (e.g. "prior_week_updated:W14" → "W14").
  const stalePriorWeek = (() => {
    const m = /^prior_week_updated:W(\d+)$/.exec(stalenessReason);
    return m ? `W${m[1]}` : null;
  })();

  // If we don't have at least 2 audits in the series, skip the card completely
  if (!siblings || !hasSeries) {
    return null;
  }

  const verdictKey = summary?.verdict || 'first_audit';
  const verdict    = VERDICT_META[verdictKey] || VERDICT_META.first_audit;
  const VerdictIcon = verdict.icon;

  return (
    <Card className="border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="series-progression-card">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2">
                Series Progression
                <span className="text-xs font-mono text-muted-foreground">
                  {seriesDisplay || 'Series'}
                </span>
              </h3>
              <p className="text-[11px] text-muted-foreground">
                {sibs.length} week{sibs.length !== 1 ? 's' : ''} tracked · click any prior week to jump to its audit
              </p>
            </div>
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={() => regenerate({ silent: false })}
            disabled={generating}
            className="gap-1.5 h-8 text-xs"
            data-testid="cross-series-refresh-button"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {summary ? 'Re-run AI analysis' : (isStale ? 'Auto-running…' : 'Run AI analysis')}
          </Button>
        </div>

        {/* Week strip */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="series-week-strip">
          {sibs.map((s, i) => (
            <React.Fragment key={s.audit_id}>
              {i > 0 && <ArrowRight className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />}
              <WeekPill sib={s} onClick={() => navigate(`/results/${s.audit_id}`)} />
            </React.Fragment>
          ))}
        </div>

        {/* AI verdict */}
        {priorCount > 0 && (
          <div className="mt-4 pt-4 border-t border-border/60">
            {loading && !summary && !generating && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading continuity analysis…
              </div>
            )}

            {generating && !summary && (
              <div
                className="flex items-start gap-3 text-xs"
                data-testid="cross-series-auto-running"
              >
                <Loader2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5 animate-spin" />
                <div>
                  <div className="font-semibold text-foreground mb-0.5">
                    Auto-analysing cross-week continuity…
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {stalePriorWeek
                      ? <>New activity detected on <span className="font-semibold">{stalePriorWeek}</span> — re-running cross-series AI verdict against {priorCount} prior week{priorCount !== 1 ? 's' : ''}.</>
                      : <>Generating fresh AI verdict against {priorCount} prior week{priorCount !== 1 ? 's' : ''}.</>}
                  </div>
                </div>
              </div>
            )}

            {!loading && !generating && !summary && (
              <div className="flex items-start gap-3 text-xs text-muted-foreground">
                <Sparkles className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-foreground mb-0.5">
                    AI continuity analysis not yet generated
                  </div>
                  <div className="text-[11px]">
                    Click <span className="font-semibold">Run AI analysis</span> above — the model will
                    cross-reference this week against {priorCount} prior week{priorCount !== 1 ? 's' : ''}
                    to flag missed commitments and SKU carry-over.
                  </div>
                </div>
              </div>
            )}

            {summary && (
              <div data-testid="cross-series-summary">
                {/* Verdict row */}
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold uppercase tracking-wider border ${verdict.cls}`}>
                    <VerdictIcon className="w-3 h-3" />
                    {verdict.label}
                  </span>
                  {autoRan && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-200"
                      title="The cross-series verdict was regenerated automatically because new activity landed on a prior-week thread"
                      data-testid="cross-series-auto-refreshed-badge"
                    >
                      <Sparkles className="w-2.5 h-2.5" />
                      Auto-refreshed{stalePriorWeek ? ` · ${stalePriorWeek} updated` : ''}
                    </span>
                  )}
                  {generating && !autoRan && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" /> regenerating…
                    </span>
                  )}
                  {summary.generated_at && (
                    <span className="text-[10px] text-muted-foreground font-mono ml-auto">
                      Analysed {new Date(summary.generated_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  )}
                </div>

                {summary.headline && (
                  <div className="text-sm font-semibold text-foreground mb-1">
                    {summary.headline}
                  </div>
                )}
                {summary.narrative && (
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {summary.narrative}
                  </p>
                )}

                <CommitmentOutcomes rows={summary.commitment_outcomes} />
                <CarryoverTable   rows={summary.carryover_skus} />

                {summary.recommended_next_action && (
                  <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-0.5">
                      Recommended next action
                    </div>
                    <div className="text-[12px] text-foreground">
                      {summary.recommended_next_action}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
